import { type KeyboardEvent, useState } from 'react'

import { AutoResizeTextarea, Button } from '@/components/ui'
import { useToast } from '@/hooks'
import { MAX_FACT_LENGTH } from './limits'

const COUNTER_SHOWN_FROM_LENGTH = 250
const MAX_VISIBLE_ROWS = 6

interface CaseFactEditorProps {
  initialText?: string
  onSubmit: (body: string) => Promise<void>
  onCancel: () => void
}

// Lukita stores a fact as one line, so line breaks never make it into the text.
const onOneLine = (text: string) => text.replace(/\s*\n\s*/g, ' ')

export const CaseFactEditor = ({
  initialText = '',
  onSubmit,
  onCancel,
}: CaseFactEditorProps) => {
  const [text, setText] = useState(initialText)
  const [isSaving, setIsSaving] = useState(false)
  const { errorToast } = useToast()
  const canSubmit = text.trim().length > 0 && !isSaving

  const submit = () => {
    if (!canSubmit) return
    setIsSaving(true)
    onSubmit(text.trim())
      .catch((error) => errorToast('Couldn’t save the fact', error))
      .finally(() => setIsSaving(false))
  }

  const submitOnEnter = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter also confirms a word in Japanese, Chinese and Korean typing. That is not a save.
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
    event.preventDefault()
    submit()
  }

  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <AutoResizeTextarea
        aria-label="Case fact"
        className="min-w-0 flex-1 rounded-none border-0 p-0 leading-6 shadow-none focus-visible:ring-0"
        placeholder="Type a fact, then press Enter"
        maxLength={MAX_FACT_LENGTH}
        maxRows={MAX_VISIBLE_ROWS}
        autoFocus
        value={text}
        onChange={(event) => setText(onOneLine(event.target.value))}
        onKeyDown={submitOnEnter}
      />
      {text.length >= COUNTER_SHOWN_FROM_LENGTH && (
        <span className="shrink-0 text-xs leading-6 tabular-nums text-muted-foreground">
          {text.length} / {MAX_FACT_LENGTH}
        </span>
      )}
      <Button theme="outline" size="xs" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        type="submit"
        size="xs"
        isLoading={isSaving}
        loadingText="Saving..."
        disabled={!canSubmit}
      >
        Save
      </Button>
    </form>
  )
}
